/**
 * ModeCard Component
 *
 * Individual mode card for the modes dialog.
 * Shows 3-panel summary, tags, and load/delete actions.
 * Built-in modes cannot be deleted but can be tagged.
 */

import { memo } from 'react';
import { Trash2, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { TagEditor } from './TagEditor';
import type { TagDefinition, ProviderParamDefinition } from '../types/image-generator.types';
import { formatModelName } from '../types/image-generator.types';
import type { ModelDefinitionListItem } from '@/lib/api';
import type { ImageMode } from '../types/image-generator-modes';

export interface ModeCardProps {
  mode: ImageMode;
  tagDefinitions: TagDefinition[];
  availableModels: ModelDefinitionListItem[];
  onLoadMode: (mode: ImageMode) => void;
  onDeleteMode: (modeId: string) => void;
  onUpdateMode: (modeId: string, updates: Partial<Pick<ImageMode, 'tags'>>) => void;
  onCreateTag: (name: string) => string;
}

function formatProvider(
  provider: string,
  modelName: string,
  models: ModelDefinitionListItem[],
): string {
  // Try to get company from entity capabilities
  const model = models.find((m) => m.modelName === modelName);
  const company = model?.capabilities?.company;
  if (typeof company === 'string') return company;

  // Fall back to known provider display names
  const displayNames: Record<string, string> = {
    openai: 'OpenAI',
    anthropic: 'Anthropic',
    google: 'Google',
    mistral: 'Mistral',
    cohere: 'Cohere',
    bfl: 'FLUX',
    recraft: 'Recraft',
    xai: 'xAI',
    stability: 'Stability AI',
  };
  return displayNames[provider] ?? provider;
}

function formatOverrides(
  config: ImageMode['panelConfigs'][number],
  models: ModelDefinitionListItem[],
): string {
  const parts: string[] = [];

  // Get param definitions from entity to use category-based detection
  const model = models.find((m) => m.modelName === config.modelName);
  const paramDefs: ProviderParamDefinition[] =
    (model?.capabilities?.providerParams as ProviderParamDefinition[] | undefined) || [];

  // Extract quality/style from providerParams using entity categories
  let quality = config.quality || '';
  let style = config.style || '';

  for (const [key, value] of Object.entries(config.providerParams || {})) {
    const def = paramDefs.find((d) => d.key === key);
    if (def?.category === 'quality' && value && value !== '' && value !== 'none') {
      quality = String(value);
    } else if (def?.category === 'style' && value && value !== '' && value !== 'none') {
      style = String(value);
    }
  }

  if (quality && quality !== 'standard') parts.push(quality);
  if (style && style !== 'vivid' && style !== 'none') parts.push(style);
  if (config.size !== '1:1') parts.push(config.size);
  return parts.length > 0 ? ` · ${parts.join(' · ')}` : '';
}

export const ModeCard = memo(function ModeCard({
  mode,
  tagDefinitions,
  availableModels,
  onLoadMode,
  onDeleteMode,
  onUpdateMode,
  onCreateTag,
}: ModeCardProps) {
  const assignedTags = tagDefinitions.filter((t) => mode.tags.includes(t.id));

  return (
    <div className="border border-border rounded-lg p-4 hover:bg-muted/50 transition-colors">
      {/* Header: Name + Delete */}
      <div className="flex items-start justify-between mb-1">
        <div className="flex-1 min-w-0">
          <h4 className="font-medium truncate">{mode.name}</h4>
        </div>
        {!mode.builtIn && (
          <div className="flex items-center gap-1 ml-2 flex-shrink-0">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDeleteMode(mode.id)}
              className="h-7 w-7 text-muted-foreground hover:text-destructive"
              title="Delete mode"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>

      {/* Description */}
      {mode.description && <p className="text-xs text-muted-foreground mb-3">{mode.description}</p>}

      {/* 3 Panel Summaries */}
      <div className="text-xs space-y-1 mb-3">
        {mode.panelConfigs.map((panelConfig, i) => (
          <div key={i} className="flex items-baseline gap-1">
            <span className="text-muted-foreground font-medium shrink-0">Panel {i + 1}:</span>
            <span className="truncate">
              {formatModelName(panelConfig.modelName, availableModels)}{' '}
              <span className="text-muted-foreground">
                ({formatProvider(panelConfig.provider, panelConfig.modelName, availableModels)})
              </span>
              <span className="text-muted-foreground">
                {formatOverrides(panelConfig, availableModels)}
              </span>
            </span>
          </div>
        ))}
      </div>

      {/* Collapsible Tags section */}
      <Collapsible className="mb-3">
        <CollapsibleTrigger className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors group">
          <ChevronDown className="h-3 w-3 transition-transform group-data-[state=open]:rotate-180" />
          <span>Tags{assignedTags.length > 0 ? ` (${assignedTags.length})` : ''}</span>
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-2 pl-4">
          <TagEditor
            assignedTagIds={mode.tags}
            tagDefinitions={tagDefinitions}
            onAssignTag={(tagId) => {
              if (!mode.tags.includes(tagId)) {
                onUpdateMode(mode.id, { tags: [...mode.tags, tagId] });
              }
            }}
            onUnassignTag={(tagId) => {
              onUpdateMode(mode.id, { tags: mode.tags.filter((t) => t !== tagId) });
            }}
            onCreateTag={(name) => {
              const newTagId = onCreateTag(name);
              onUpdateMode(mode.id, { tags: [...mode.tags, newTagId] });
              return newTagId;
            }}
          />
        </CollapsibleContent>
      </Collapsible>

      {/* Load button */}
      <Button variant="outline" size="sm" onClick={() => onLoadMode(mode)} className="w-full">
        Load Mode
      </Button>
    </div>
  );
});
